import { EventBus } from "../events/EventBus";
import { uuid } from "../utils/Uuid";
import { ContextFlow } from "./ContextFlow";
import { FlowDefinition } from "./FlowDefinition";
import { NestedFlowStep } from "./NestedFlowStep";
import { LocalGetCtx, StepDefinition } from "./StepDefinition";
import { StepFlow } from "./StepFlow";
import { FlowPersistence, SerializableFlowState, SerializableStepState, ExecutionPhase } from "./FlowPersistence";

// =============================================================================
// FlowRunner: Executes the workflow by managing sequence and states.
// =============================================================================
export class FlowRunner {
  public id: string; // Unique identifier for the runner
  private flowDefinition: FlowDefinition // Holds the flow definition
  private eventBus: EventBus; // EventBus instance for communication
  private currentSteps: StepFlow[] = []; // Tracks currently active steps
  private state: "running" | "paused" | "cancelled" | "exited" = "running"; // State of the flow execution
  private _contextRepository = new Map<string, ContextFlow>();
  setContext(context: ContextFlow) {
    this._contextRepository.set("__flow__", context);
  }
  public verbose: boolean = false; // Verbose logging toggle

  // Holds reconciliation steps to ensure a single instance is used.
  public pendingReconciliationSteps: Map<string, StepFlow> = new Map();

  private offEvts: Array<{ off: () => void }> = [];

  // Persistence support
  private enablePersistence: boolean = false;
  private saveStateTimer: ReturnType<typeof setTimeout> | null = null;
  private executionLogs: Array<{
    flowId: string;
    stepName: string;
    method: string;
    stepId: string;
    timestamp: string;
  }> = [];
  private currentStepExecutionPhase: Map<string, string> = new Map();

  constructor(
    flowDefinition: FlowDefinition,
    eventBus: EventBus,
    enablePersistence: boolean = false
  ) {
    this.id = uuid(); // Assign a unique ID
    this.flowDefinition = flowDefinition;
    this.eventBus = eventBus;
    this.enablePersistence = enablePersistence;
    this._contextRepository.set("__flow__", new ContextFlow());
  }

  // Executes the flow from the defined starting step
  async execute(): Promise<void> {
    if (!this.flowDefinition.startStep) {
      throw new Error('Flow has no defined start step');
    }

    const startDef = this.resolveStepDefinition(this.flowDefinition.startStep);
    const startInstance = this.instantiateStep(startDef);
    this.currentSteps.push(startInstance);

    // register for exitFlow and cancel step
    console.log('Listen to step:${startInstance.id}:exitFlow');
    this.offEvts.push(this.eventBus.on({
      eventName: "step:exitFlow",
      handler: (payload) => {
        for (const step of this.currentSteps) {
          if (step.id == (payload.data as any)['stepId']) {
            this.flushPendingSave();
            this.state = "exited";
            this.logStepExecution(step, "onExitFlow");
            step.onCancel();
            step.next();
          }
        }
      }
    }));

    // register for exitFlow and cancel step
    this.offEvts.push(this.eventBus.on({
      eventName: "step:cancel",
      handler: (payload) => {
        for (const step of this.currentSteps) {
          if (step.id == (payload.data as any)['stepId']) {
            this.flushPendingSave();
            this.state = "cancelled";
            this.logStepExecution(step, "onCancel");
            step.onCancel();
            step.next();
          }
        }
      }
    }));

    await this.runStep(startInstance);

    for (const offHandler of this.offEvts) {
      offHandler.off();
    }
  }

  // Resolves a step name to its definition, either a simple step or a nested flow
  private resolveStepDefinition(stepName: string): StepDefinition {
    if (this.flowDefinition.getSteps().has(stepName)) {
      const step = this.flowDefinition.getSteps().get(stepName);
      return step!;
    }
    throw `Step definition not found for ${stepName}`;
  }

  // Creates an instance of a step or nested flow step
  private instantiateStep(def: StepDefinition): StepFlow {
    if (def.stepClass) {
      const step = new (def.stepClass as { new(bus: EventBus): StepFlow; })(this.eventBus);
      if (def.contextIsolation) {
        !this._contextRepository.has(step.stepName) && (this._contextRepository.set(step.stepName, new ContextFlow()));

        if (def.contextKeepAlive) {
          step.forceContext(this._contextRepository.get(step.stepName)!);
          return step;
        }

        step.forceContext(new ContextFlow());
        return step;
      }
      step.forceContext(this._contextRepository.get("__flow__")!);
      return step;
    } else if (def.stepFlow) {
      const nestedStep = new NestedFlowStep(this.eventBus, def.stepFlow!);
      nestedStep.verbose = this.verbose;
      if (def.contextIsolation) {
        !this._contextRepository.has(nestedStep.stepName) && (this._contextRepository.set(nestedStep.stepName, new ContextFlow()));

        if (def.contextKeepAlive) {
          nestedStep.forceContext(this._contextRepository.get(nestedStep.stepName)!);
          return nestedStep;
        }

        nestedStep.forceContext(new ContextFlow());
        return nestedStep;
      }
      nestedStep.forceContext(this._contextRepository.get("__flow__")!);
      return nestedStep;
    }
    throw 'Unknown step type';
  }

  // --------------------------------------------------
  // Runs a step, handling reconciliation when required.
  // --------------------------------------------------
  private async runStep(
    step: StepFlow,
    previousStep?: StepFlow
  ): Promise<void> {
    if (this.state !== "running") return;
    let targetStep = step;

    // If the step requires reconciliation, reuse the same instance.
    if (previousStep && step.requiresReconciliation) {
      if (this.pendingReconciliationSteps.has(step.stepName)) {
        targetStep = this.pendingReconciliationSteps.get(step.stepName)!;
      } else {
        this.pendingReconciliationSteps.set(step.stepName, step);
        targetStep = step;
      }
      // Set input and process reconciliation state
      const shouldProceed = await targetStep.processReconciliation(previousStep.stepName);
      if (!shouldProceed) {
        if (this.verbose) {
          console.log(`[${step.stepName}] Waiting for additional inputs...`);
        }
        return;
      }
    }

    // Execute the (possibly reconciled) step.
    await this.executeStep(targetStep);

    const nextDefs = this.resolveNextSteps(step);
    await this.executeNextSteps(step, nextDefs);
  }

  // --------------------------------------------------
  // Executes a step, waiting for explicit completion via next().
  // Handles reconciliation execution separately.
  // --------------------------------------------------
  private async executeStep(step: StepFlow): Promise<void> {
    await this.logStepExecution(step, "onInit");
    await step.onInit();

    await this.logStepExecution(step, "onEventSetup");
    await step.onEventSetup();

    // Wait for explicit completion signal via next()
    const stepCompletion = new Promise<void>((resolve) => {
      const offNext = this.eventBus.on({
        eventName: `step:${step.stepName}:${step.id}:next`,
        handler: () => {
          offNext.off();
          resolve();
        }
      });
    });

    if (step.requiresReconciliation) {
      await this.logStepExecution(step, "onReconciliationExecute");
      await step.onReconciliationExecute();

      // Ensure the step has signaled next() before proceeding.
      if (!step.hasSignaledNext) {
        step.next();
      }
    } else {
      try {
        await this.logStepExecution(step, "onExecute");
        await step.onExecute();
      } catch (error) {
        await this.logStepExecution(step, "onError");
        await step.onError(error);
      }
    }

    // Wait for step to signal completion
    await stepCompletion;

    await this.logStepExecution(step, "onEventTearDown");
    await step.onEventTearDown();

    await this.logStepExecution(step, "onExit");
    await step.onExit();
  }

  // Executes the next steps after the current step has completed
  private async executeNextSteps(
    currentStep: StepFlow,
    nextDefs: Array<StepDefinition>
  ): Promise<void> {
    await Promise.all(nextDefs.map(async (nextDef) => {
      const nextInstance = this.instantiateStep(nextDef);
      this.currentSteps.push(nextInstance);
      await this.runStep(nextInstance, currentStep);
    }));
  }

  // Determines the next steps in the flow based on transitions and links
  private resolveNextSteps(
    step: StepFlow,
    previousOutput?: any
  ): Array<StepDefinition> {
    let nextDefs: Array<StepDefinition> = [];
    const stepName = step.stepName;

    // Get transitions from the flow definition
    const transitions = this.flowDefinition.getTransitions();
    if (transitions.has(step.stepName)) {
      const transition = transitions.get(step.stepName)!;
      transition.getCtx = <T>(key: string, stepName?: string) => {
        if (stepName && this._contextRepository.has(stepName)) {
          return this._contextRepository.get(stepName)?.get<T>(key);
        } else {
          return this._contextRepository.get("__flow__")?.get<T>(key);
        }
      };

      transition.setCtx = <T>(key: string, value: T, stepName?: string) => {
        if (stepName && this._contextRepository.has(stepName)) {
          this._contextRepository.get(stepName)?.set<T>(key, value);
        } else {
          this._contextRepository.get("__flow__")?.set<T>(key, value);
        }
      };
      const exitSteps = transition.onExecute();

      for (const dest of exitSteps) {
        if (this.flowDefinition.getSteps().has(dest)) {
          nextDefs.push(this.flowDefinition.getSteps().get(dest)!);
        } /*else if (this.flowDefinition.getNestedFlows().has(dest)) {
          nextDefs.push(this.flowDefinition.getNestedFlows().get(dest)!);
        }*/
      }

      return nextDefs;
    }

    // If no transitions provided next steps, use default links.
    if (nextDefs.length === 0) {
      const links = this.flowDefinition.getLinks();
      const nextNames = links.get(stepName) || [];
      for (const ns of nextNames) {
        if (this.flowDefinition.getSteps().has(ns)) {
          const def = this.flowDefinition.getSteps().get(ns)!;
          if (!def.canExecute(
            function <T>(this: FlowRunner, key: string): T | undefined {
              if (def.stepName && this._contextRepository.has(def.stepName)) {
                return this._contextRepository.get(def.stepName)?.get<T>(key);
              }
              return this._contextRepository.get("__flow__")?.get<T>(key);
            }.bind(this) as LocalGetCtx,
            function <T>(this: FlowRunner, key: string): T | undefined {
              return this._contextRepository.get("__flow__")?.get<T>(key);
            }.bind(this) as LocalGetCtx
          )) continue;
          if (def.isExclusive) return [def];
          nextDefs.push(def);
        }
      }
    }
    return nextDefs;
  }

  // Logs step execution events
  private async logStepExecution(step: StepFlow, method: string): Promise<void> {
    const logData = {
      flowId: this.id,
      stepName: step.stepName,
      method,
      stepId: step.id,
      timestamp: new Date().toISOString()
    };
    // Store execution phase for persistence
    this.currentStepExecutionPhase.set(step.id, method);

    // Store log for persistence
    if (this.enablePersistence) {
      this.executionLogs.push(logData);
    }
    this.eventBus.emit({ eventName: "log:stepExecution", data: logData });
    if (this.verbose) {
      this.localLog(`Step ${logData.stepName} (${logData.stepId}) - ${method} executed`);
    }

    // Save state after each significant step
    if (this.enablePersistence && ['onInit', 'onEventSetup', 'onExecute', 'onReconciliationExecute'].includes(method)) {
      this.scheduleSaveState();
    }
  }

  // Flush a pending debounced save synchronously. Must run BEFORE the flow
  // leaves the "running" state (exit/cancel handlers): saveState() refuses to
  // persist a dead flow, so without this the final step transition before an
  // exit would never reach localStorage.
  private flushPendingSave(): void {
    if (this.saveStateTimer !== null) {
      clearTimeout(this.saveStateTimer);
      this.saveStateTimer = null;
      this.saveState();
    }
  }

  // Coalesce the 3-4 saveState() calls fired per step transition (and bursts
  // across parallel steps) into a single trailing serialization. saveState()
  // reads live state at flush time, so the last write always wins.
  // ponytail: a refresh within the 150ms window loses that last transition —
  // restore then resumes from the previous saved state, which the 5-minute
  // MAX_AGE recovery already tolerates.
  private scheduleSaveState(): void {
    if (this.saveStateTimer !== null) return;
    this.saveStateTimer = setTimeout(() => {
      this.saveStateTimer = null;
      this.saveState();
    }, 150);
  }

  private localLog(message: string): void {
    console.log(`[FlowRunner ${this.id}] ${message}`);
  }

  // Pauses the execution of the flow
  pause(): void {
    this.state = "paused";
    this.currentSteps.forEach(step => {
      this.logStepExecution(step, "onPause");
      step.onPause();
    });
  }

  // Resumes execution of the flow
  resume(): void {
    this.state = "running";
    this.currentSteps.forEach(step => {
      this.logStepExecution(step, "onResume");
      step.onResume();
    });
  }

  // Cancels execution of the flow
  cancel(): void {
    this.state = "cancelled";
    this.currentSteps.forEach(step => {
      this.logStepExecution(step, "onCancel");
      step.onCancel();
      step.next();
    });

    // Clear persisted state when cancelled — and drop any pending debounced
    // save so it can't resurrect the state we just cleared.
    if (this.enablePersistence) {
      if (this.saveStateTimer !== null) {
        clearTimeout(this.saveStateTimer);
        this.saveStateTimer = null;
      }
      FlowPersistence.clearFlowState(this.flowDefinition.name);
    }
  }

  // Enable persistence for this flow runner
  enableFlowPersistence(): void {
    this.enablePersistence = true;
  }

  // Save current flow state to localStorage
  private saveState(): void {
    if (!this.enablePersistence) return;
    // A debounced flush can fire after the flow ended — never persist a dead
    // flow (it would resurrect state that cancel()/close just cleared).
    if (this.state === "exited" || this.state === "cancelled") return;

    try {
      const serializedSteps: SerializableStepState[] = this.currentSteps.map(step => ({
        stepName: step.stepName,
        stepId: step.id,
        hasSignaledNext: step.hasSignaledNext,
        requiresReconciliation: step.requiresReconciliation,
        reconciliationState: this.serializeMap((step as any).reconciliationState),
        receivedData: this.serializeMap((step as any).receivedData),
        context: FlowPersistence.serializeContext((step as any)._context?.store || {}),
        executionPhase: (this.currentStepExecutionPhase.get(step.id) || 'onInit') as ExecutionPhase,
        timestamp: new Date().toISOString()
      }));

      const contextRepository: Record<string, Record<string, any>> = {};
      this._contextRepository.forEach((context, key) => {
        contextRepository[key] = FlowPersistence.serializeContext((context as any).store || {});
      });

      const flowState: SerializableFlowState = {
        flowId: this.id,
        flowName: this.flowDefinition.name,
        state: this.state,
        currentSteps: serializedSteps,
        contextRepository,
        executionLogs: [...this.executionLogs],
        startStep: this.flowDefinition.startStep,
        timestamp: new Date().toISOString(),
      };

      FlowPersistence.saveFlowState(this.id, flowState);
    } catch (error) {
      console.warn(`[FlowRunner] Failed to save state:`, error);
    }
  }

  // Restore flow state from localStorage
  static async restoreFromState(
    flowDefinition: FlowDefinition,
    eventBus: EventBus,
    flowState: SerializableFlowState
  ): Promise<FlowRunner> {
    const runner = new FlowRunner(flowDefinition, eventBus, true);
    runner.id = flowState.flowId;
    runner.state = flowState.state;
    runner.executionLogs = [...flowState.executionLogs];

    // Restore context repository
    flowState.contextRepository && Object.entries(flowState.contextRepository).forEach(([key, contextData]) => {
      const context = new ContextFlow();
      Object.entries(contextData).forEach(([contextKey, value]) => {
        (context as any).store.set(contextKey, value);
      });
      runner._contextRepository.set(key, context);
    });

    // Generic handling for flows with missing parallel steps
    // If only 1 step was saved but the flow definition has multiple parallel steps available,
    // we might need to restore the missing parallel steps
    if (flowState.currentSteps.length === 1) {
      const savedStepName = flowState.currentSteps[0].stepName;

      // Check if this step has parallel siblings by looking at the flow definition
      const missingParallelSteps = runner.findMissingParallelSteps(savedStepName, flowState.currentSteps);

      if (missingParallelSteps.length > 0) {
        console.log(`[FlowRunner] ${flowDefinition.name} restoration: Found ${missingParallelSteps.length} missing parallel steps:`, missingParallelSteps);

        // Add the missing parallel steps
        for (const stepName of missingParallelSteps) {
          console.log(`[FlowRunner] Adding missing parallel step: ${stepName}`);
          flowState.currentSteps.push({
            stepName: stepName,
            stepId: uuid(),
            hasSignaledNext: false,
            requiresReconciliation: false,
            context: {},
            executionPhase: 'onInit', // Start from the beginning
            timestamp: new Date().toISOString()
          });
        }
        console.log(`[FlowRunner] ${flowDefinition.name} restoration: Now have ${flowState.currentSteps.length} steps`);
      }
    }

    // Restore current steps (but don't execute them yet)
    for (const stepState of flowState.currentSteps) {
      try {
        const stepDef = runner.resolveStepDefinition(stepState.stepName);
        const stepInstance = runner.instantiateStep(stepDef);

        // Restore step properties
        stepInstance.id = stepState.stepId;
        stepInstance.hasSignaledNext = stepState.hasSignaledNext;
        stepInstance.requiresReconciliation = stepState.requiresReconciliation;

        // Restore reconciliation state if applicable
        if (stepState.reconciliationState) {
          (stepInstance as any).reconciliationState = new Map(Object.entries(stepState.reconciliationState));
        }
        if (stepState.receivedData) {
          (stepInstance as any).receivedData = new Map(Object.entries(stepState.receivedData));
        }

        // Restore context
        if (stepState.context) {
          const context = new ContextFlow();
          Object.entries(stepState.context).forEach(([key, value]) => {
            (context as any).store.set(key, value);
          });
          stepInstance.forceContext(context);
        }

        runner.currentSteps.push(stepInstance);
        // Store the execution phase directly (no conversion needed)
        runner.currentStepExecutionPhase.set(stepInstance.id, stepState.executionPhase);
      } catch (error) {
        console.warn(`[FlowRunner] Failed to restore step ${stepState.stepName}:`, error);
      }
    }

    return runner;
  }

  // Resume execution from restored state
  async resumeFromState(): Promise<void> {
    if (this.state !== "running") {
      console.warn(`[FlowRunner] Cannot resume flow in state: ${this.state}`);
      return;
    }

    // Set up event handlers
    this.setupEventHandlers();

    // If we have multiple steps, they should be executed in parallel (like in normal execution)
    // This handles flows with parallel steps correctly
    if (this.currentSteps.length > 1) {
      console.log(`[FlowRunner] Resuming ${this.currentSteps.length} parallel steps:`,
        this.currentSteps.map(s => s.stepName));

      // Execute all steps concurrently
      await Promise.all(this.currentSteps.map(async (step) => {
        const phase = this.currentStepExecutionPhase.get(step.id) || 'onInit';

        try {
          console.log(`[FlowRunner] Resuming ${step.stepName} from phase: ${phase}`);
          await this.resumeStepByPhase(step, phase);
        } catch (error) {
          console.error(`[FlowRunner] Failed to resume step ${step.stepName}:`, error);
          await step.onError(error);
        }
      }));
    } else {
      // Single step - resume normally
      for (const step of this.currentSteps) {
        const phase = this.currentStepExecutionPhase.get(step.id) || 'onInit';

        try {
          await this.resumeStepByPhase(step, phase);
        } catch (error) {
          console.error(`[FlowRunner] Failed to resume step ${step.stepName}:`, error);
          await step.onError(error);
        }
      }
    }

    // Clean up event handlers
    for (const offHandler of this.offEvts) {
      offHandler.off();
    }
  }

  // Generic method to resume a step based on its execution phase
  private async resumeStepByPhase(step: StepFlow, phase: string): Promise<void> {
    switch (phase) {
      case 'onInit':
        // For parallel steps, use executeStep directly instead of runStep
        // to avoid sequential execution logic
        if (this.currentSteps.length > 1) {
          await this.executeStep(step);
        } else {
          await this.resumeStepFromInit(step);
        }
        break;
      case 'onEventSetup':
        await this.resumeStepFromEventSetup(step);
        break;
      case 'onExecute':
        await this.resumeStepFromExecute(step);
        break;
      case 'onReconciliationExecute':
        await this.resumeStepFromReconciliation(step);
        break;
      case 'onExit':
        // Step was already completed, skip
        break;
      case 'onError':
        // Restart from beginning for error states
        if (this.currentSteps.length > 1) {
          await this.executeStep(step);
        } else {
          await this.resumeStepFromInit(step);
        }
        break;
      default:
        // Default to restarting from beginning
        if (this.currentSteps.length > 1) {
          await this.executeStep(step);
        } else {
          await this.resumeStepFromInit(step);
        }
    }
  }

  // Helper methods for resuming steps
  private async resumeStepFromInit(step: StepFlow): Promise<void> {
    await this.runStep(step);
  }

  private async resumeStepFromEventSetup(step: StepFlow): Promise<void> {
    // Step was in event setup phase, continue from execute
    await this.logStepExecution(step, "onExecute");
    await step.onExecute();
    step.next();
  }

  private async resumeStepFromExecute(step: StepFlow): Promise<void> {
    // If the step was in execute phase, it means it was actively executing
    // We need to continue from where it left off
    if (!step.hasSignaledNext) {
      // Set up the step completion handler like in normal execution
      const stepCompletion = new Promise<void>((resolve) => {
        const offNext = this.eventBus.on({
          eventName: `step:${step.stepName}:${step.id}:next`,
          handler: () => {
            offNext.off();
            resolve();
          }
        });
      });

      // The step was executing, so we need to re-establish the UI and event handlers
      // If it's a reconciliation step, call onReconciliationExecute
      if (step.requiresReconciliation) {
        await this.logStepExecution(step, "onReconciliationExecute");
        await step.onReconciliationExecute();
        if (!step.hasSignaledNext) {
          step.next();
        }
      } else {
        // For regular steps, we need to re-establish event handlers and UI components
        // After page refresh, all event subscriptions and DOM elements are gone
        try {
          // Re-run onEventSetup to restore event handlers
          await this.logStepExecution(step, "onEventSetup");
          await step.onEventSetup();

          // Then re-run onExecute to restore UI components
          await this.logStepExecution(step, "onExecute");
          await step.onExecute();
        } catch (error) {
          await this.logStepExecution(step, "onError");
          await step.onError(error);
        }
      }

      // Wait for completion
      await stepCompletion;

      // Continue with teardown
      await this.logStepExecution(step, "onEventTearDown");
      await step.onEventTearDown();

      await this.logStepExecution(step, "onExit");
      await step.onExit();

      // Process next steps
      const nextDefs = this.resolveNextSteps(step);
      await this.executeNextSteps(step, nextDefs);
    }
  }

  private async resumeStepFromReconciliation(step: StepFlow): Promise<void> {
    // Step was in reconciliation, continue from there
    await this.logStepExecution(step, "onReconciliationExecute");
    await step.onReconciliationExecute();
    if (!step.hasSignaledNext) {
      step.next();
    }
  }

  private setupEventHandlers(): void {
    // Set up the same event handlers as in execute()
    this.offEvts.push(this.eventBus.on({
      eventName: "step:exitFlow",
      handler: (payload) => {
        for (const step of this.currentSteps) {
          if (step.id == (payload.data as any)['stepId']) {
            this.flushPendingSave();
            this.state = "exited";
            this.logStepExecution(step, "onExitFlow");
            step.onCancel();
            step.next();
          }
        }
      }
    }));

    this.offEvts.push(this.eventBus.on({
      eventName: "step:cancel",
      handler: (payload) => {
        for (const step of this.currentSteps) {
          if (step.id == (payload.data as any)['stepId']) {
            this.flushPendingSave();
            this.state = "cancelled";
            this.logStepExecution(step, "onCancel");
            step.onCancel();
            step.next();
          }
        }
      }
    }));
  }


  // Find missing parallel steps by analyzing the flow definition
  private findMissingParallelSteps(savedStepName: string, currentSteps: any[]): string[] {
    const missingSteps: string[] = [];

    try {
      // Look for the parent step that leads to parallel steps
      // We need to find which step transitions to multiple parallel steps
      const links = this.flowDefinition.getLinks();

      // Find a step that has multiple outgoing links (indicating parallel execution)
      for (const [sourceStep, targetSteps] of links.entries()) {
        if (targetSteps.length > 1) {
          // This step has multiple targets (parallel execution)
          // Check if our saved step is one of these parallel targets
          if (targetSteps.includes(savedStepName)) {
            // Found the parallel group! Add missing steps from this group
            for (const parallelStep of targetSteps) {
              const isAlreadyRestored = currentSteps.some(step => step.stepName === parallelStep);
              if (!isAlreadyRestored) {
                // Verify the step actually exists in the flow definition
                if (this.flowDefinition.getSteps().has(parallelStep)) {
                  missingSteps.push(parallelStep);
                }
              }
            }
            break; // Found the parallel group, no need to continue
          }
        }
      }
    } catch (error) {
      console.warn(`[FlowRunner] Error finding missing parallel steps:`, error);
    }

    return missingSteps;
  }

  private serializeMap(map: Map<string, any> | undefined): Record<string, any> {
    if (!map) return {};
    const obj: Record<string, any> = {};
    map.forEach((value, key) => {
      obj[key] = value;
    });
    return obj;
  }
}